import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrTierResponse } from '@/lib/api';

interface UseSubscriptionTiersReturn {
  tiers: GrTierResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useSubscriptionTiers(): UseSubscriptionTiersReturn {
  const [tiers, setTiers] = useState<GrTierResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchTiers = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getGameRewardsTiers();
      if (fetchIdRef.current !== id) return;
      setTiers(result.tiers);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load tiers');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchTiers();
  }, [fetchTiers]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { tiers, loading, error, refresh };
}
